/**
 * Fixed wake strip during ACA scale-to-zero.
 * - Does not replace page content (Landing LCP stays the hero).
 * - position:fixed → no CLS when it appears.
 * - Skipped on pure marketing routes Google measures most.
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useApiReady } from '../../hooks/useApiReady';
import { ensureApiReady } from '../../api/apiClient';

const MARKETING_PATHS = new Set([
  '/',
  '/pricing',
  '/how-it-works',
  '/why-us',
  '/roadmap',
  '/blog',
  '/privacy',
  '/cookies',
  '/feature-soon',
  '/solutions',
  '/faq',
]);

function isMarketingPath(pathname: string): boolean {
  if (MARKETING_PATHS.has(pathname)) return true;
  if (pathname.startsWith('/blog/')) return true;
  if (pathname.startsWith('/solutions/')) return true;
  return false;
}

const SHOW_AFTER_MS = 1800;

const ApiWakeBanner: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { warming, ready, failed } = useApiReady();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Keep warming in background even on marketing (silent for CWV).
    if (!ready && !warming && !failed) {
      void ensureApiReady();
    }
  }, [ready, warming, failed]);

  useEffect(() => {
    if (isMarketingPath(pathname) || ready || !warming) {
      setVisible(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [pathname, ready, warming]);

  if (!visible || ready || isMarketingPath(pathname)) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="api-wake-banner"
      className="fixed bottom-4 left-1/2 z-[80] w-[min(28rem,calc(100%-1.5rem))] -translate-x-1/2 pointer-events-none"
    >
      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-gray-950/95 px-4 py-3 shadow-xl shadow-black/40">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary-light" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-medium text-white leading-snug">
            {t('errors.api_wake.title')}
          </p>
          <p className="text-xs text-gray-400 leading-snug mt-0.5">
            {t('errors.api_wake.message')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ApiWakeBanner;
